/**
 * QuestionScreen — Milestone 37.4 · the single conversational surface
 * for one brain-mission question.
 *
 * Locked layout order (per user's Additional UX Requirement):
 *
 *   1. Question — the conversational label
 *   2. Why this matters — a sentence teaching the founder why Akinex
 *      is asking
 *   3. Importance — Required / Recommended / Optional badge
 *   4. If skipped — the founder-safe consequence
 *   5. Input — the answer surface
 *   6. Example answers — optional illustrations
 *
 * The primary CTA and Skip control live on the MissionShell footer,
 * not inside this screen. The screen is pure content: it renders the
 * question and captures the answer into local state; the parent page
 * commits on Save.
 */
import { useEffect, useMemo, useState } from "react";

import type { BrainQuestion } from "@/entities/brain-missions";

const CATEGORY_STYLES: Record<BrainQuestion["category"], {
  label: string;
  chipClass: string;
  dotClass: string;
}> = {
  required: {
    label: "Required",
    chipClass: "border-cyan-300/40 bg-cyan-400/10 text-cyan-100",
    dotClass: "bg-cyan-300",
  },
  recommended: {
    label: "Recommended",
    chipClass: "border-violet-300/40 bg-violet-400/10 text-violet-100",
    dotClass: "bg-violet-300",
  },
  optional: {
    label: "Optional",
    chipClass: "border-white/15 bg-white/[0.04] text-white/70",
    dotClass: "bg-white/50",
  },
};

interface QuestionScreenProps {
  question: BrainQuestion;
  /** Current stored answer, if any (used to pre-fill input). */
  currentAnswer: unknown;
  /** Notifies the parent whenever the local draft changes. */
  onDraftChange: (value: unknown, isDirty: boolean) => void;
  /** True when the parent is mid-flight persisting. */
  isPending?: boolean;
}

export function QuestionScreen({
  question,
  currentAnswer,
  onDraftChange,
  isPending = false,
}: QuestionScreenProps) {
  const [draft, setDraft] = useState<string>(() => toStringDraft(question, currentAnswer));

  // Reset local draft when the question changes (advance forward /
  // backward through the mission).
  useEffect(() => {
    setDraft(toStringDraft(question, currentAnswer));
  }, [question.id]);

  const style = CATEGORY_STYLES[question.category];

  const initialStringDraft = useMemo(
    () => toStringDraft(question, currentAnswer),
    [question.id, currentAnswer],
  );

  const handleChange = (next: string) => {
    setDraft(next);
    onDraftChange(coerceForSubmit(question, next), next !== initialStringDraft);
  };

  return (
    <div
      className="mx-auto flex max-w-2xl flex-col gap-6 text-white"
      data-testid={`brain-question-screen-${question.id}`}
      data-question-id={question.id}
      data-question-category={question.category}
    >
      {/* 1. Question — the conversational label. */}
      <h2
        className="text-2xl font-semibold leading-snug tracking-tight text-white sm:text-3xl"
        data-testid="brain-question-label"
      >
        {question.label}
      </h2>

      {/* 2 & 3 — importance chip + why-this-matters teach line. */}
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <span
            className={
              "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 "
              + "text-[10px] font-semibold uppercase tracking-[0.14em] "
              + style.chipClass
            }
            data-testid="brain-question-category-chip"
          >
            <span className={"h-1.5 w-1.5 rounded-full " + style.dotClass} />
            {style.label}
          </span>
        </div>
        <div
          className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 text-sm leading-relaxed text-white/85"
          data-testid="brain-question-why"
        >
          <div className="mb-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-cyan-300/80">
            Why this matters
          </div>
          {question.why_it_matters}
        </div>
      </div>

      {/* 4. If skipped — the founder-safe consequence. */}
      <div
        className="rounded-xl border border-dashed border-white/10 bg-white/[0.02] px-4 py-3 text-xs text-white/60"
        data-testid="brain-question-impact"
      >
        <span className="font-semibold text-white/80">If you skip: </span>
        {question.impact_of_skipping}
      </div>

      {/* 5. Input. */}
      <div className="flex flex-col gap-2">
        {question.kind === "long_text" || question.kind === "string_list" ? (
          <textarea
            value={draft}
            onChange={(e) => handleChange(e.target.value)}
            rows={question.kind === "string_list" ? 5 : 6}
            placeholder={
              question.kind === "string_list"
                ? "One per line — or separated with commas."
                : "Type in your own words. Akinex will keep it as-is."
            }
            className="w-full resize-y rounded-xl border border-white/15 bg-[color:var(--brand-obsidian,#0B0D12)] px-4 py-3 text-sm text-white placeholder-white/30 outline-none focus-visible:border-cyan-300 focus-visible:ring-2 focus-visible:ring-cyan-300/40 disabled:opacity-60"
            data-testid="brain-question-input"
            disabled={isPending}
          />
        ) : (
          <input
            type="text"
            value={draft}
            onChange={(e) => handleChange(e.target.value)}
            placeholder="Type in your own words."
            className="w-full rounded-xl border border-white/15 bg-[color:var(--brand-obsidian,#0B0D12)] px-4 py-3 text-sm text-white placeholder-white/30 outline-none focus-visible:border-cyan-300 focus-visible:ring-2 focus-visible:ring-cyan-300/40 disabled:opacity-60"
            data-testid="brain-question-input"
            disabled={isPending}
          />
        )}
      </div>

      {/* 6. Example answers — never used as defaults. */}
      {question.example_answers.length > 0 && (
        <details
          className="rounded-xl border border-white/10 bg-white/[0.02] px-4 py-3 text-xs text-white/60"
          data-testid="brain-question-examples"
        >
          <summary className="cursor-pointer font-semibold text-white/80">
            See a couple of examples
          </summary>
          <ul className="mt-2 space-y-1.5 pl-4">
            {question.example_answers.map((ex) => (
              <li key={ex} className="list-disc text-white/70">{ex}</li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

function toStringDraft(q: BrainQuestion, value: unknown): string {
  if (value == null) return "";
  if (q.kind === "string_list") {
    if (Array.isArray(value)) return value.join("\n");
    if (typeof value === "string") return value;
    return "";
  }
  if (typeof value === "string") return value;
  return "";
}

function coerceForSubmit(q: BrainQuestion, draft: string): unknown {
  if (q.kind === "string_list") {
    return draft
      .split(/\n|,/)
      .map((s) => s.trim())
      .filter(Boolean);
  }
  return draft.trim();
}

/** Detect a non-empty draft — used by the parent to gate the primary CTA. */
export function isDraftAnswered(_q: BrainQuestion, draft: unknown): boolean {
  if (draft == null) return false;
  if (Array.isArray(draft)) return draft.length > 0;
  if (typeof draft === "string") return draft.trim().length > 0;
  return true;
}
